import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { businessApi } from '../services/api';
import { Business } from '../types';
import { Modal } from '../components/Modal';

const CATEGORIES = [
  'Gimnasio','CrossFit','Yoga','Pilates','Natación','Boxeo',
  'Artes Marciales','Danza','Bienestar','Fisioterapia',
  'Padel','Fútbol','Atletismo','Ciclismo','Escalada','Otra',
];

interface BusinessForm {
  name: string;
  description: string;
  category: string;
  address: string;
  city: string;
  phone: string;
  email: string;
  website: string;
  instagram: string;
  whatsapp: string;
}

export function Businesses() {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState<Business | null>(null);

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<BusinessForm>();

  useEffect(() => { fetchBusinesses(); }, []);

  const fetchBusinesses = async () => {
    try {
      const r = await businessApi.getMyBusinesses();
      setBusinesses(r.data.data.businesses || []);
    } catch { toast.error('Error cargando negocios'); }
    finally { setIsLoading(false); }
  };

  const onSubmit = async (data: BusinessForm) => {
    try {
      if (editingBusiness) {
        await businessApi.updateBusiness(editingBusiness.id, data as any);
        toast.success('Negocio actualizado');
      } else {
        await businessApi.createBusiness(data as any);
        toast.success('Negocio creado ✅');
      }
      setIsModalOpen(false);
      setEditingBusiness(null);
      reset();
      fetchBusinesses();
    } catch (e: any) {
      const msg = e?.response?.data?.message || (editingBusiness ? 'Error al actualizar' : 'Error al crear');
      toast.error(msg);
    }
  };

  const handleEdit = (b: Business) => {
    setEditingBusiness(b);
    reset({
      name: b.name || '',
      description: b.description || '',
      category: (b as any).category || '',
      address: b.address || '',
      city: (b as any).city || '',
      phone: b.phone || '',
      email: b.email || '',
      website: b.website || '',
      instagram: (b as any).instagram || '',
      whatsapp: (b as any).whatsapp || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar este negocio? Esta acción no se puede deshacer.')) return;
    try {
      await businessApi.deleteBusiness(id);
      toast.success('Negocio eliminado');
      fetchBusinesses();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleAddNew = () => {
    setEditingBusiness(null);
    reset({ name:'',description:'',category:'',address:'',city:'',phone:'',email:'',website:'',instagram:'',whatsapp:'' });
    setIsModalOpen(true);
  };

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      <style>{`
        .biz-card { background: white; border-radius: 20px; border: 1px solid #e2e8f0; overflow: hidden; transition: transform .2s, box-shadow .2s; }
        .biz-card:hover { transform: translateY(-3px); box-shadow: 0 12px 32px rgba(0,0,0,.08); }
        .biz-cover { height: 120px; background: linear-gradient(135deg, #1e293b, #334155); position: relative; overflow: hidden; }
        .biz-cover img { width: 100%; height: 100%; object-fit: cover; }
        .biz-logo { position: absolute; bottom: -20px; left: 16px; width: 44px; height: 44px; border-radius: 10px; border: 3px solid white; background: linear-gradient(135deg, #3b82f6, #8b5cf6); display: flex; align-items: center; justify-content: center; font-weight: 700; color: white; font-size: 1.1rem; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,.15); }
        .biz-logo img { width: 100%; height: 100%; object-fit: cover; }
        .biz-body { padding: 28px 16px 16px; }
        .biz-name { font-size: 1rem; font-weight: 700; color: #1e293b; margin-bottom: 4px; }
        .biz-meta { font-size: .78rem; color: #94a3b8; margin-bottom: 12px; }
        .biz-stats { display: flex; gap: 16px; padding: 12px 0; border-top: 1px solid #f1f5f9; border-bottom: 1px solid #f1f5f9; margin-bottom: 14px; }
        .biz-stat { text-align: center; flex: 1; }
        .biz-stat-num { font-size: 1.2rem; font-weight: 700; color: #1e293b; }
        .biz-stat-label { font-size: .68rem; color: #94a3b8; margin-top: 2px; }
        .biz-actions { display: flex; gap: 8px; }
        .cat-badge { display: inline-flex; background: #dbeafe; color: #1d4ed8; font-size: .68rem; font-weight: 700; padding: 2px 8px; border-radius: 100px; margin-bottom: 8px; }
      `}</style>

      {/* HEADER */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Mis negocios</h1>
          <p className="text-secondary-500 text-sm mt-1">Gestiona tus negocios y su presencia en FitPulse</p>
        </div>
        <button onClick={handleAddNew} className="btn-primary flex items-center gap-2">
          <PlusIcon className="h-5 w-5" />Nuevo negocio
        </button>
      </div>

      {businesses.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {businesses.map(b => (
            <div key={b.id} className="biz-card">
              <div className="biz-cover">
                {(b as any).coverImage && <img src={(b as any).coverImage} alt={b.name} />}
                <div className="biz-logo">
                  {(b as any).logo ? <img src={(b as any).logo} alt={b.name}/> : b.name.charAt(0).toUpperCase()}
                </div>
              </div>
              <div className="biz-body">
                {(b as any).category && <span className="cat-badge">{(b as any).category}</span>}
                <div className="biz-name">{b.name}</div>
                <div className="biz-meta">
                  {(b as any).city && `📍 ${(b as any).city}`}
                  {b.phone && ` · 📞 ${b.phone}`}
                </div>
                <div className="biz-stats">
                  <div className="biz-stat"><div className="biz-stat-num">{b._count?.services || 0}</div><div className="biz-stat-label">Servicios</div></div>
                  <div className="biz-stat"><div className="biz-stat-num">{b._count?.staff || 0}</div><div className="biz-stat-label">Personal</div></div>
                  <div className="biz-stat"><div className="biz-stat-num">{b._count?.bookings || 0}</div><div className="biz-stat-label">Reservas</div></div>
                </div>
                <div className="biz-actions">
                  <a href={`/businesses/${b.id}`} className="btn-secondary text-sm flex-1 flex items-center justify-center gap-1">
                    <EyeIcon className="h-4 w-4"/>Gestionar
                  </a>
                  <button onClick={() => handleEdit(b)} className="btn-secondary text-sm px-3">
                    <PencilIcon className="h-4 w-4"/>
                  </button>
                  <button onClick={() => handleDelete(b.id)} style={{padding:'8px 10px',borderRadius:8,background:'#fef2f2',color:'#ef4444',border:'none',cursor:'pointer'}}>
                    <TrashIcon className="h-4 w-4"/>
                  </button>
                </div>
                <a href={`https://admin.fitpulse.es/b/${(b as any).slug}`} target="_blank" rel="noreferrer"
                  style={{display:'flex',alignItems:'center',gap:4,marginTop:10,fontSize:'.75rem',color:'#3b82f6',textDecoration:'none',fontWeight:600}}>
                  <GlobeAltIcon style={{width:14}}/>Ver página pública
                </a>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card p-12 text-center">
          <div style={{fontSize:'3.5rem',marginBottom:16}}>🏢</div>
          <p className="text-secondary-500 font-medium mb-2">No tienes negocios todavía</p>
          <p className="text-secondary-400 text-sm mb-6">Crea tu primer negocio para aparecer en el directorio de FitPulse</p>
          <button onClick={handleAddNew} className="btn-primary inline-flex items-center gap-2">
            <PlusIcon className="h-5 w-5"/>Crear mi primer negocio
          </button>
        </div>
      )}

      {/* MODAL */}
      <Modal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setEditingBusiness(null); }} title={editingBusiness ? 'Editar negocio' : 'Nuevo negocio'} maxWidth="lg">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Nombre del negocio *</label>
              <input {...register('name', { required: 'El nombre es obligatorio' })} className="input" placeholder="Ej: CrossFit Madrid Centro" />
              {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>}
            </div>
            <div>
              <label className="label">Categoría</label>
              <select {...register('category')} className="input">
                <option value="">— Seleccionar —</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="label">Descripción</label>
            <textarea {...register('description')} className="input" rows={3} placeholder="Describe tu negocio, qué ofreces, tu filosofía..." />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Ciudad</label>
              <input {...register('city')} className="input" placeholder="Madrid" />
            </div>
            <div>
              <label className="label">Dirección</label>
              <input {...register('address')} className="input" placeholder="Calle Mayor 1" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Teléfono</label>
              <input {...register('phone')} type="tel" className="input" placeholder="+34 600 000 000" />
            </div>
            <div>
              <label className="label">Email</label>
              <input {...register('email')} type="email" className="input" placeholder="info@tunegocio.com" />
            </div>
          </div>

          <div>
            <label className="label">Web</label>
            <input {...register('website')} className="input" placeholder="https://tunegocio.com" />
          </div>

          <div style={{borderTop:'1px solid #e2e8f0',paddingTop:16}}>
            <p className="label mb-3">Redes sociales</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">📸 Instagram (usuario)</label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 border border-r-0 border-secondary-300 bg-secondary-50 text-secondary-500 text-sm rounded-l-lg">@</span>
                  <input {...register('instagram')} className="input rounded-l-none" placeholder="tunegocio" />
                </div>
              </div>
              <div>
                <label className="label">💬 WhatsApp</label>
                <input {...register('whatsapp')} className="input" placeholder="34600000000" />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => { setIsModalOpen(false); setEditingBusiness(null); }} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? 'Guardando...' : editingBusiness ? 'Actualizar negocio' : 'Crear negocio'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
